import { GoogleSignin, statusCodes } from 'react-native-google-signin';
import auth from '@react-native-firebase/auth';
import { getConfig } from '../../config';
import { openInAppWebViewer } from '../../utils/inAppWebViewer';
import { Logger } from '../../utils/Logger';

export const setupGoogleSignin = async (configOptions): Promise<void> => {
  Logger.debug('[SocialAuth] configure GoogleSignin');
  GoogleSignin.configure(configOptions);
};

export const getGoogleLoginCredential = async (): Promise<?Object> => {
  const { useWebBreakoutForGoogleAuth } = getConfig().auth;

  if (useWebBreakoutForGoogleAuth) {
    return getGoogleLoginCredentialUsingWebBreakout();
  } else {
    return getGoogleLoginCredentialUsingGoogleSignIn();
  }
};

const getGoogleLoginCredentialUsingWebBreakout = async (): Promise<?Object> => {
  // await here to show the spinner while the web is launching. the site will deep link back into the app
  const { googleAuthWebBreakoutUrl } = getConfig().pages;
  Logger.leaveBreadcrumb('Google login breakout', {
    url: googleAuthWebBreakoutUrl,
  });
  await openInAppWebViewer(googleAuthWebBreakoutUrl);
}

const getGoogleLoginCredentialUsingGoogleSignIn = async (): Promise<?Object> => {
  const getCurrentUserOrNull = async () => {
    try {
      return await GoogleSignin.signInSilently();
    } catch (error) {
      if (error.code === statusCodes.SIGN_IN_REQUIRED) {
        Logger.debug('[SocialAuth] user is not signed in');
        return null;
      }

      throw error;
    }
  };

  try {
    let data = await getCurrentUserOrNull();
    if (!data) {
      Logger.debug('[SocialAuth] calling signIn');
      data = await GoogleSignin.signIn();
    }

    const tokens = await GoogleSignin.getTokens();
    if (data && tokens) {
      // todo: we can export and use getCredentialFromTokens and remove the need to import auth here
      return auth.GoogleAuthProvider.credential(tokens.idToken, tokens.accessToken);
    }
  } catch (error) {
    if (error.code === statusCodes.SIGN_IN_CANCELLED) {
      Logger.debug('[SocialAuth] signin cancelled, exiting gracefully');
      return null;
    }
    if (error.message.match(/^RNGoogleSignInError: .*Domain=com.google.GIDSignIn Code=-1.*general error -3.*/)) {
      // known bug in iOS SDK 5, where signin canceled is not handled correctly
      // https://github.com/googlesamples/google-services/issues/426
      Logger.debug('[SocialAuth] iOS workaround on SDK 5.0.0 for canceled login flow');
      return null;
    }

    Logger.debug(`[SocialAuth] signin error ${error}`);
    throw error;
  }

  Logger.debug('[SocialAuth] unknown result');
  return null;
};

export const signOutOfGoogle = async (): Promise<void> => {
  const { useWebBreakoutForGoogleAuth } = getConfig().auth;

  Logger.leaveBreadcrumb('GoogleSignin.signOut');
  GoogleSignin.signOut();

  if (useWebBreakoutForGoogleAuth) {
    if (isLoggedInUsingGoogle()) {
      Logger.debug('[SocialAuth] signing out of Google account in web');
      // todo: this is not the pretiest of logout flows
      //  after the user logged out, a (in app) browser opens
      //  showing "Choose Google account", with the then signed out user listed
      //  dont know when this is done: the user is stuck on this page
      //  and needs to close the tab to see Fitillion again (welcome screen)
      //  can handle /ServiceLogin, but that will just ask for app/Chrome (and not always?)
      //  cant timeout and close the tab, because code here is only running when openInAppWebViewer is done
      await openInAppWebViewer('https://accounts.google.com/Logout');
    }
  }
};

const isLoggedInUsingGoogle = () => {
  const user = auth().currentUser;
  if (!user) {
    return false;
  }

  const googleProvider = user.providerData
    .find(data => data.providerId === auth.GoogleAuthProvider.PROVIDER_ID);
  return !!googleProvider;
};
